<template>
  <v-container fluid class="ev-settings-page">
    <!-- Alert Banner -->
    <AAlertBanner variant="danger" :messages="alertError" title="Warning" />

    <!-- Header Card -->
    <div class="ev-header-card">
      <div class="ev-header-card__info" v-if="!loadingDialog">
        <h1 class="ev-header-card__title">Event Settings</h1>
        <span class="ev-header-card__subtitle">{{ model.view.name }}</span>
      </div>
    </div>

    <!-- Underline Tabs + Content -->
    <div class="ev-content-card">
      <div class="ev-underline-tabs">
        <button
          class="ev-underline-tab"
          :class="{ 'ev-underline-tab--active': activeTab === 0 }"
          @click="activeTab = 0"
        >
          <v-icon size="16" class="mr-1">mdi-calendar-star</v-icon>
          {{ model.view.name }}
        </button>
        <button
          class="ev-underline-tab"
          :class="{ 'ev-underline-tab--active': activeTab === 1 }"
          @click="activeTab = 1"
        >
          <v-icon size="16" class="mr-1">mdi-format-list-bulleted-square</v-icon>
          Event Category
        </button>
      </div>

      <div class="ev-tab-body">
        <v-tabs-items v-model="activeTab">
          <v-tab-item>
            <component
              :is="eventTab"
              :params="params"
              @toggleEventPage="toggleEventPage"
            ></component>
          </v-tab-item>
          <v-tab-item>
            <component
              :is="eventCategoryTab"
              :params="params"
              @toggleEventCategoryPage="toggleEventCategoryPage"
            ></component>
          </v-tab-item>
        </v-tabs-items>
      </div>
    </div>

    <HrdcLoader :loadingDialog="loadingDialog"></HrdcLoader>
  </v-container>
</template>

<script>
import Api from "@/objects/api";
import { mapState } from "vuex";
import EventAdd from "./event/Add";
import Model from "@/objects/model";
import EventIndex from "./event/Index";
import Service from "@/objects/service";
import hrdcFunction from "@/objects/hrdcFunction";
import EventCategoryAdd from "./event_category/Add";
import EventCategoryIndex from "./event_category/Index";
import HrdcData from "@/services/hrdc_event/objects/globalData";

export default {
  mixins: [hrdcFunction],
  components: {
    EventIndex,
    EventAdd,
    EventCategoryIndex,
    EventCategoryAdd,
  },
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
  },
  props: ["params"],
  data: () => ({
    apiSubmit: new Api(),
    model: new Model(),
    service: new Service(),
    hrdcData: new HrdcData(),
    alertError: [],
    activeTab: 0,
    date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10),
    menu: false,
    formData: {
      applicationNo: null,
    },
    errorField: {
      applicationNo: null,
    },
    loadingDialog: false,
    btnDisabled: false,
    eventTab: "EventIndex",
    eventCategoryTab: "EventCategoryIndex",
  }),
  async created() {
    this.showLoadingDialog();
    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);

    // Assign apiDetails by mapping repository.
    const apiDetails = {
      apiSubmit: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/claim_approved/update-claim-approved`,
        method: "post",
      },
    };

    for (const api in apiDetails) {
      if (apiDetails[api].url !== undefined) {
        this[api].setUrl(apiDetails[api].url);
      }
      if (apiDetails[api].method !== undefined) {
        this[api].setMethod(apiDetails[api].method);
      }
      if (apiDetails[api].params !== undefined) {
        this[api].setParams(apiDetails[api].params);
      }
    }

    this.stages = await this.hrdcData.allStages;
    this.rolesAssignation(this.auth.roles);

    this.hideLoadingDialog();
  },
  methods: {
    clearErrorMsg() {
      for (let error in this.errorField) {
        this.errorField[error] = null;
      }
    },
    showErrorMessage(response) {
      for (const key in response.data) {
        this.errorField[key] = null;
        this.errorField[key] = response.data[key];
      }
    },
    redirectBack() {
      let route = this.eventRoute("Settings");
      if (this.params.backTo) {
        route = this.params.backTo;
      }

      this.$router.push({
        name: route,
        params: {
          modelKey: this.model.key,
          backTo: "",
        },
      });
    },
    toggleEventPage(redirectToComponent) {
      this.eventTab = redirectToComponent;
    },
    toggleEventCategoryPage(redirectToComponent) {
      this.eventCategoryTab = redirectToComponent;
    },
    async submit() {
      this.loadingDialog = !this.loadingDialog;
      let data = {
        applicationUuid: this.formData.applicationNo,
        claimApprovedDate: this.formData.claimApprovedDate,
        referById: this.auth.uuid,
        editMode: this.editMode,
      };

      this.apiSubmit.setParams(data);
      this.apiSubmit.setCallbackCompleted((response) => {
        try {
          this.clearErrorMsg();
          this.loadingDialog = !this.loadingDialog;
          if (!response.status) {
            this.showErrorMessage(response);
            this.$store.dispatch("showMessage", {
              message: response.message,
              messageType: "error",
              timeout: 2000,
            });
          }

          if (response.status) {
            this.$store.commit("assignDeveloperData", {
              paginationPageNo: this.$route.query.currentPageNo,
            });

            this.$router.push({
              name: this.eventRoute("Application"),
              query: {
                viewId: this.$store.state[this.service.key].data.viewId,
              },
            });
            this.$store.dispatch("showMessage", {
              message: response.data,
              messageType: "success",
              timeout: 2000,
            });
          }
        } catch (err) {
          console.log(err);
        }
      });
      this.apiSubmit.fetch();
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .ev-settings-page {
  background-color: var(--app-page);
}

.dark-mode .ev-header-card {
  background: var(--app-card);
}

.dark-mode .ev-back-btn {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
}

.dark-mode .ev-back-btn:hover {
  background: var(--app-elevated);
  border-color: var(--app-border-strong);
}

.dark-mode .ev-header-card__title {
  color: var(--app-text);
}

.dark-mode .ev-header-card__subtitle {
  color: var(--app-text-faint);
}

.dark-mode .ev-content-card {
  background: var(--app-card);
}

.dark-mode .ev-underline-tabs {
  border-bottom: 1px solid var(--app-border);
  background: var(--app-card);
}

.dark-mode .ev-underline-tab {
  color: var(--app-text-muted);
}

.dark-mode .ev-underline-tab:hover {
  color: var(--app-text);
  background: var(--app-surface);
}

.dark-mode .ev-underline-tab--active {
  background: var(--app-accent-soft);
}

/* >>> end auto-dark <<< */

/* ============================================
   PAGE WRAPPER
   ============================================ */
.ev-settings-page {
  background-color: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding: 28px 32px 60px !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ============================================
   HEADER CARD
   ============================================ */
.ev-header-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04), 0 4px 20px rgba(0, 0, 0, 0.03);
  padding: 20px 24px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.ev-back-btn {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.ev-back-btn:hover {
  background: #e2e8f0;
  border-color: #cbd5e1;
  transform: translateX(-2px);
}

.ev-header-card__info {
  flex: 1;
  min-width: 0;
}

.ev-header-card__title {
  font-size: 1.15rem;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  letter-spacing: -0.3px;
  line-height: 1.3;
}

.ev-header-card__subtitle {
  font-size: 0.78rem;
  color: #94a3b8;
  font-weight: 500;
  margin-top: 2px;
  display: block;
}

/* ============================================
   CONTENT CARD (holds tabs + body)
   ============================================ */
.ev-content-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04), 0 4px 20px rgba(0, 0, 0, 0.03);
  overflow: hidden;
}

/* ============================================
   TAB BODY
   ============================================ */
.ev-tab-body {
  padding: 0;
}

/* ============================================
   TRANSITIONS
   ============================================ */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.2s ease;
}
.slide-fade-enter {
  transform: translateY(-8px);
  opacity: 0;
}
.slide-fade-leave-to {
  transform: translateY(-4px);
  opacity: 0;
}

/* ============================================
   DARK MODE
   ============================================ */
.dark-mode .ev-settings-page { background-color: var(--app-page); }
.dark-mode .ev-header-card { background: var(--app-card); box-shadow: 0 1px 4px var(--app-shadow-soft); }
.dark-mode .ev-header-card__title { color: var(--app-text); }
.dark-mode .ev-header-card__subtitle { color: var(--app-text-faint); }
.dark-mode .ev-back-btn { background: var(--app-page); border-color: var(--app-border); }
.dark-mode .ev-back-btn:hover { background: var(--app-card); border-color: var(--app-border-strong); }
/* ============================================
   UNDERLINE TABS
   ============================================ */
.ev-underline-tabs {
  display: flex;
  gap: 4px;
  padding: 14px 24px 0;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
}

.ev-underline-tab {
  display: inline-flex;
  align-items: center;
  padding: 10px 18px;
  border: none;
  background: none;
  cursor: pointer;
  font-family: 'Lato', sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  color: #64748b;
  border-bottom: 2px solid transparent;
  transition: all 0.15s;
  margin-bottom: -1px;
}

.ev-underline-tab:hover {
  color: #334155;
  background: #f8fafc;
}

.ev-underline-tab--active {
  color: #0ea5e9;
  border-bottom-color: #0ea5e9;
  background: #f0f9ff;
}

.ev-underline-tab--active .v-icon {
  color: #0ea5e9 !important;
}

.dark-mode .ev-content-card { background: var(--app-card); box-shadow: 0 1px 4px var(--app-shadow-soft); }
.dark-mode .ev-underline-tabs { border-color: var(--app-border); background: var(--app-card); }
.dark-mode .ev-underline-tab { color: var(--app-text-muted); }
.dark-mode .ev-underline-tab:hover { color: var(--app-text); background: var(--app-border); }
.dark-mode .ev-underline-tab--active { color: var(--app-accent); border-bottom-color: var(--app-accent); background: var(--app-accent-soft); }
</style>

<style>
/* Hide Vuetify tabs-items transition wrapper border */
.ev-content-card .v-tabs-items {
  background: transparent !important;
}
</style>
